import * as THREE from 'three'
import Experience from '../Experience.js'

/**
 * Candle
 * Loads the GLTF candle jar model and places it at the scene origin.
 * Exposes this.model for scroll-driven tilt (rotation.z) applied in PR-4.
 *
 * Model file: static/models/candle/candle.gltf
 */
export default class Candle {
    constructor() {
        this.experience = new Experience()
        this.scene      = this.experience.scene
        this.resources  = this.experience.resources
        this.time       = this.experience.time
        this.debug      = this.experience.debug

        this.sizes   = this.experience.sizes

        this.setModel()
        this.setDebug()
    }

    getBaseScale() {
        // Mobile (<768px): 20% smaller than desktop
        return this.sizes.width < 768 ? 0.016 : 0.02
    }

    setModel() {
        const gltf = this.resources.items['candleModel']
        if (!gltf) {
            console.error('[Candle] candleModel failed to load — check static/models/candle/candle.gltf')
            this.model = new THREE.Group()
            this.scene.add(this.model)
            return
        }
        this.model = gltf.scene

        // Model was exported from Blender at large scale (~22 Blender units tall)
        // Scale down so the candle is ~2.5 Three.js units tall (mobile: -20%)
        this.baseScale = this.getBaseScale()
        this.model.scale.setScalar(this.baseScale)
        this.model.position.set(0, -1, 0)

        // Traverse all meshes: enable shadows + warm material tweaks
        this.model.traverse((child) => {
            if (child.isMesh) {
                child.castShadow    = true
                child.receiveShadow = true

                if (child.material) {
                    child.material.envMapIntensity = 0.4
                }
            }
        })

        this.scene.add(this.model)

        // Responsive scale: update when viewport crosses the mobile breakpoint
        this.sizes.on('resize', () => {
            const s = this.getBaseScale()
            if (s !== this.baseScale) {
                this.baseScale = s
                this.model.scale.setScalar(s)
            }
        })
    }

    setDebug() {
        if (!this.debug.active) return
        const folder = this.debug.ui.addFolder('Candle')
        folder.add(this.model.position, 'y').min(-3).max(3).step(0.01).name('Y pos')
        folder.add(this.model.rotation, 'z').min(-Math.PI).max(Math.PI).step(0.01).name('Tilt Z')
        folder.add(this.model.scale, 'x')
            .min(0.01).max(0.5).step(0.001).name('Scale')
            .onChange((v) => { this.model.scale.set(v, v, v) })
    }

    update() {
        // Slow idle bob: subtle Y oscillation when not scroll-pinned
        if (!this.isPinned) {
            this.model.position.y = -0.5 + Math.sin(this.time.elapsed * 0.0008) * 0.04
        }

    }
}
